import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const TRANSACTION_TYPES = [
  { value: 'buy', label: 'Buy' },
  { value: 'sell', label: 'Sell' },
  { value: 'dividend', label: 'Dividend' },
  { value: 'transfer', label: 'Transfer' },
  { value: 'fee', label: 'Fee' },
];

/**
 * TransactionForm — modal form for creating a new transaction.
 */
const TransactionForm = ({ goals, brokers, assets, onSubmit, onClose }) => {
  const [form, setForm] = useState({
    goal: '',
    broker: '',
    asset: '',
    transaction_type: 'buy',
    quantity: '',
    price_per_unit: '',
    commission: '0',
    taxes: '0',
    transaction_date: new Date().toISOString().slice(0, 16),
    notes: '',
  });
  const [totalAmount, setTotalAmount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const selectedGoal = goals.find((g) => String(g.id) === String(form.goal)) || null;
  const hasGoalTransactions = selectedGoal && (selectedGoal.broker_ids?.length > 0 || selectedGoal.asset_ids?.length > 0);
  const selectedBroker = brokers.find((b) => String(b.id) === String(form.broker)) || null;
  const brokerCash = selectedBroker ? parseFloat(selectedBroker.cash_balance || 0) : null;
  const selectedAsset = assets.find((a) => String(a.id) === String(form.asset)) || null;
  const sharesHeld = selectedAsset ? parseFloat(selectedAsset.total_shares_held || 0) : null;

  const filteredBrokers = hasGoalTransactions
    ? brokers.filter((b) => selectedGoal.broker_ids.includes(b.id))
    : brokers;

  const filteredAssets = (hasGoalTransactions && form.transaction_type !== 'buy')
    ? assets.filter((a) => selectedGoal.asset_ids.includes(a.id))
    : assets;

  // Auto-calculate total amount
  useEffect(() => {
    const qty = parseFloat(form.quantity) || 0;
    const price = parseFloat(form.price_per_unit) || 0;
    setTotalAmount(qty * price);
  }, [form.quantity, form.price_per_unit]);

  const validate = () => {
    const errs = {};
    if (!form.goal) errs.goal = 'Goal is required.';
    if (!form.broker) errs.broker = 'Broker is required.';
    if (!form.asset) errs.asset = 'Asset is required.';
    if (!form.quantity || parseFloat(form.quantity) <= 0) errs.quantity = 'Quantity must be > 0.';
    if (!form.price_per_unit || parseFloat(form.price_per_unit) <= 0) errs.price_per_unit = 'Price must be > 0.';
    if (!form.transaction_date) errs.transaction_date = 'Date is required.';

    if (form.transaction_type === 'sell' && sharesHeld !== null) {
      const qty = parseFloat(form.quantity) || 0;
      if (qty > sharesHeld) {
        errs.quantity = `Cannot sell more than you hold. Available: ${sharesHeld} shares.`;
      }
      if (sharesHeld === 0) {
        errs.quantity = 'You have no shares of this asset to sell.';
      }
    }

    if (form.transaction_type === 'buy' && brokerCash !== null) {
      const totalCost = totalAmount + (parseFloat(form.commission) || 0) + (parseFloat(form.taxes) || 0);
      if (totalCost > brokerCash) {
        errs.total_amount = `Insufficient funds. Available: $${brokerCash.toFixed(2)}, needed: $${totalCost.toFixed(2)}.`;
      }
    }

    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    setLoading(true);
    setErrors({});
    try {
      await onSubmit({
        goal: parseInt(form.goal),
        broker: parseInt(form.broker),
        asset: parseInt(form.asset),
        transaction_type: form.transaction_type,
        quantity: parseFloat(form.quantity),
        price_per_unit: parseFloat(form.price_per_unit),
        commission: parseFloat(form.commission) || 0,
        taxes: parseFloat(form.taxes) || 0,
        transaction_date: new Date(form.transaction_date).toISOString(),
        notes: form.notes || null,
      });
    } catch (err) {
      const apiErrors = err.response?.data || {};
      if (typeof apiErrors === 'object') {
        setErrors(apiErrors);
      } else {
        setErrors({ non_field_errors: 'Submission failed. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const setField = (key, value) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'goal') {
        const g = goals.find((g) => String(g.id) === String(value));
        const brokerIds = g?.broker_ids || [];
        const assetIds = g?.asset_ids || [];
        if (brokerIds.length && !brokerIds.includes(parseInt(prev.broker))) next.broker = '';
        if (assetIds.length && !assetIds.includes(parseInt(prev.asset))) next.asset = '';
      }
      return next;
    });
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
      <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h2 className="text-lg font-bold text-gray-900">New Transaction</h2>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Transaction Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
            <div className="grid grid-cols-5 gap-1">
              {TRANSACTION_TYPES.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setField('transaction_type', t.value)}
                  className={`py-2 text-xs font-medium rounded-lg transition ${
                    form.transaction_type === t.value
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Goal Select */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Investment Goal</label>
            <select
              value={form.goal}
              onChange={(e) => setField('goal', e.target.value)}
              className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white ${errors.goal ? 'border-red-400' : 'border-gray-200'}`}
            >
              <option value="">Select a goal...</option>
              {goals.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
            {errors.goal && <p className="mt-1 text-xs text-red-500">{errors.goal}</p>}
          </div>

          {/* Broker Select */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Broker
              {hasGoalTransactions && <span className="ml-1 text-xs text-blue-500">(asociados al goal)</span>}
            </label>
            <select
              value={form.broker}
              onChange={(e) => setField('broker', e.target.value)}
              className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white ${errors.broker ? 'border-red-400' : 'border-gray-200'}`}
            >
              <option value="">Select a broker...</option>
              {filteredBrokers.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}{b.cash_balance !== undefined ? ` — $${parseFloat(b.cash_balance).toFixed(2)} disp.` : ''}
                </option>
              ))}
            </select>
            {errors.broker && <p className="mt-1 text-xs text-red-500">{errors.broker}</p>}
          </div>

          {/* Asset Select */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Asset
              {hasGoalTransactions && form.transaction_type !== 'buy' && <span className="ml-1 text-xs text-blue-500">(asociados al goal)</span>}
            </label>
            <select
              value={form.asset}
              onChange={(e) => setField('asset', e.target.value)}
              className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white ${errors.asset ? 'border-red-400' : 'border-gray-200'}`}
            >
              <option value="">Select an asset...</option>
              {filteredAssets.map((a) => (
                <option key={a.id} value={a.id}>{a.symbol} – {a.name}</option>
              ))}
            </select>
            {errors.asset && <p className="mt-1 text-xs text-red-500">{errors.asset}</p>}
          </div>

          {/* Quantity & Price */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">Quantity</label>
                {form.transaction_type === 'sell' && sharesHeld !== null && sharesHeld > 0 && (
                  <button
                    type="button"
                    onClick={() => setField('quantity', String(sharesHeld))}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Sell all ({sharesHeld})
                  </button>
                )}
              </div>
              <input
                type="number"
                value={form.quantity}
                onChange={(e) => setField('quantity', e.target.value)}
                className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none ${errors.quantity ? 'border-red-400' : 'border-gray-200'}`}
                placeholder="0"
                min="0"
                max={form.transaction_type === 'sell' && sharesHeld !== null ? sharesHeld : undefined}
                step="any"
              />
              {sharesHeld !== null && form.transaction_type === 'sell' && !errors.quantity && (
                <p className="mt-1 text-xs text-gray-500">Available: {sharesHeld} shares</p>
              )}
              {sharesHeld !== null && sharesHeld > 0 && form.transaction_type === 'buy' && (
                <p className="mt-1 text-xs text-gray-500">Holding: {sharesHeld} shares</p>
              )}
              {errors.quantity && <p className="mt-1 text-xs text-red-500">{errors.quantity}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Price per Unit ($)</label>
              <input
                type="number"
                value={form.price_per_unit}
                onChange={(e) => setField('price_per_unit', e.target.value)}
                className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none ${errors.price_per_unit ? 'border-red-400' : 'border-gray-200'}`}
                placeholder="0.00"
                min="0"
                step="any"
              />
              {errors.price_per_unit && <p className="mt-1 text-xs text-red-500">{errors.price_per_unit}</p>}
            </div>
          </div>

          {/* Total Preview */}
          {(() => {
            const commission = parseFloat(form.commission) || 0;
            const taxes = parseFloat(form.taxes) || 0;
            const totalCost = totalAmount + commission + taxes;
            const isBuy = form.transaction_type === 'buy';
            const exceeded = isBuy && brokerCash !== null && totalCost > brokerCash;
            return (
              <div className={`rounded-xl p-3 border ${exceeded ? 'bg-red-50 border-red-300' : 'bg-blue-50 border-blue-200'}`}>
                <div className="flex justify-between items-center">
                  <span className={`text-sm font-medium ${exceeded ? 'text-red-700' : 'text-blue-700'}`}>Total Amount</span>
                  <span className={`text-lg font-bold ${exceeded ? 'text-red-800' : 'text-blue-800'}`}>
                    ${totalAmount.toFixed(2)}
                  </span>
                </div>
                {isBuy && brokerCash !== null && (
                  <div className="flex justify-between items-center mt-1">
                    <span className={`text-xs ${exceeded ? 'text-red-600' : 'text-blue-500'}`}>
                      Available in broker
                    </span>
                    <span className={`text-xs font-semibold ${exceeded ? 'text-red-600' : 'text-green-600'}`}>
                      ${brokerCash.toFixed(2)}
                    </span>
                  </div>
                )}
                {exceeded && (
                  <p className="mt-1 text-xs text-red-600 font-medium">
                    Fondos insuficientes. Faltan ${(totalCost - brokerCash).toFixed(2)}.
                  </p>
                )}
                {errors.total_amount && (
                  <p className="mt-1 text-xs text-red-500">{errors.total_amount}</p>
                )}
              </div>
            );
          })()}

          {/* Commission & Taxes */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Commission ($)</label>
              <input
                type="number"
                value={form.commission}
                onChange={(e) => setField('commission', e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Taxes ($)</label>
              <input
                type="number"
                value={form.taxes}
                onChange={(e) => setField('taxes', e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </div>
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Transaction Date</label>
            <input
              type="datetime-local"
              value={form.transaction_date}
              onChange={(e) => setField('transaction_date', e.target.value)}
              className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none ${errors.transaction_date ? 'border-red-400' : 'border-gray-200'}`}
            />
            {errors.transaction_date && <p className="mt-1 text-xs text-red-500">{errors.transaction_date}</p>}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
            <textarea
              value={form.notes}
              onChange={(e) => setField('notes', e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none resize-none"
              rows={2}
              placeholder="Any additional notes..."
            />
          </div>

          {errors.non_field_errors && (
            <p className="text-xs text-red-500">{errors.non_field_errors}</p>
          )}
          {errors.detail && (
            <p className="text-xs text-red-500">{errors.detail}</p>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-3 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 transition"
            >
              {loading ? 'Saving...' : 'Record Transaction'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TransactionForm;
